import type { AdminViewStatsDto } from '@carcheck/shared';
import Box from '@mui/material/Box';
import type { ReactElement, ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { tokens } from '../../../design';

/** One row of a top-10 list; a router link only when `to` is given. */
export interface ViewsTopRowProps {
  rank: number;
  lead: ReactNode;
  name: string;
  sub?: string | null;
  chip?: string | null;
  count: ReactElement | string;
  to?: string | undefined;
}

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1.25,
  py: 0.75,
  color: 'inherit',
  textDecoration: 'none',
  minWidth: 0,
} as const;

export const ViewsTopRow = ({
  rank,
  lead,
  name,
  sub,
  chip,
  count,
  to,
}: ViewsTopRowProps): ReactElement => {
  const body = (
    <>
      <Box
        sx={{ width: 18, fontSize: 12, color: tokens.text.tertiary, fontFamily: tokens.font.mono }}
      >
        {rank}
      </Box>
      {lead}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontSize: 13,
            fontWeight: 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {name}
        </Box>
        {sub ? <Box sx={{ fontSize: 11, color: tokens.text.tertiary }}>{sub}</Box> : null}
      </Box>
      {chip ? (
        <Box
          component="span"
          sx={{
            fontSize: 10,
            fontWeight: 700,
            px: 1,
            py: 0.25,
            borderRadius: 999,
            background: tokens.surface.muted,
            color: tokens.text.secondary,
          }}
        >
          {chip}
        </Box>
      ) : null}
      <Box sx={{ fontFamily: tokens.font.mono, fontSize: 13, fontWeight: 700, textAlign: 'right' }}>
        {count}
      </Box>
    </>
  );
  return to ? (
    <Box component={Link} to={to} sx={rowSx}>
      {body}
    </Box>
  ) : (
    <Box sx={rowSx}>{body}</Box>
  );
};

const LEAD_SIZE = 32;

/** Thumbnail / avatar: image when present, otherwise the fallback glyph. */
export const ViewsLead = ({
  url,
  fallback,
  round,
}: {
  url: string | null;
  fallback: string;
  round?: boolean;
}): ReactElement => (
  <Box
    sx={{
      width: LEAD_SIZE,
      height: LEAD_SIZE,
      flexShrink: 0,
      borderRadius: round ? 999 : '8px',
      background: tokens.surface.muted,
      color: tokens.text.secondary,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 11,
      fontWeight: 700,
      overflow: 'hidden',
    }}
  >
    {url ? (
      <Box
        component="img"
        src={url}
        alt=""
        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
      />
    ) : (
      fallback
    )}
  </Box>
);

/** Props shared by the three view tiles. */
export interface ViewsTileProps {
  data: AdminViewStatsDto | undefined;
  isLoading: boolean;
  isError: boolean;
  gridColumn: string;
}
